import type { CartPanelItem } from '../../hooks/useCartPanelItems'
import type { CartPanelItemCartStatus } from '../../utils/cartPanelUtils'
import type { TripListPrice, TripListRow, TripListRowType } from './tripListTypes'

const LE_PRICED_ROW_TYPES: ReadonlySet<TripListRowType> = new Set(['accommodation', 'experience', 'flight'])

export function getTripListRowTripItemId(row: TripListRow): number {
  return row.kind === 'item' ? row.item.id : row.option.itemId
}

export function isTripListRowMatchingCartPanelItem(row: TripListRow, item: CartPanelItem): boolean {
  const tripItemId = getTripListRowTripItemId(row)
  if (row.kind === 'transport') {
    return item.productType === 'flight' && item.pairedTripItemIds.includes(tripItemId)
  }
  return item.tripItemId === tripItemId
}

function isCartPanelPriceUsable(rowPrice: TripListPrice, item: CartPanelItem): boolean {
  if (item.pricing.price === undefined) return false
  return !item.pricing.loading || rowPrice.price === undefined
}

// Narrow, like CartPanelPriceEstimateItem in cartPanelUtils.ts -- lets a caller outside this
// file (cartItemAssistantPrices.ts) satisfy this without depending on the full CartPanelItem
// union.
export interface CartPanelExactPriceItem {
  source: 'cart' | 'itinerary'
  cartStatus: CartPanelItemCartStatus
}

// Exported for cartItemAssistantPrices.ts -- the assistant-facing price fact needs the same
// exact-vs-indicative distinction the List row itself renders as a "From" prefix, so it
// doesn't hand the model a bare number that reads as a total when it's really a per-unit
// estimate (see isBookingReady on CartItemPrice).
export function isExactCartPanelPrice(item: CartPanelExactPriceItem): boolean {
  return item.source === 'cart' && item.cartStatus === 'active'
}

function getCartPanelPriceCurrency(rowPrice: TripListPrice, currentCurrency: string): string | undefined {
  return currentCurrency || rowPrice.currency
}

function getCartPanelRowPrice(
  rowPrice: TripListPrice,
  item: CartPanelItem,
  currentCurrency: string,
): TripListPrice {
  // Unsynced flight rows already derive per-person prices from the saved
  // party total. The cart panel amount is a whole-party total.
  if (item.productType === 'flight' && !isExactCartPanelPrice(item)) return rowPrice

  const displayType = isExactCartPanelPrice(item) ? 'total' : 'from'

  return {
    price: item.pricing.price,
    memberPrice: item.pricing.memberPrice,
    currency: getCartPanelPriceCurrency(rowPrice, currentCurrency),
    saleUnit: displayType === 'total' ? undefined : rowPrice.saleUnit,
    displayType,
  }
}

function shouldUseCartPanelPricing(row: TripListRow): boolean {
  return LE_PRICED_ROW_TYPES.has(row.rowType)
}

export function applyCartPanelPricesToTripListRows(
  rows: Array<TripListRow>,
  cartPanelItems: Array<CartPanelItem>,
  currentCurrency: string,
): Array<TripListRow> {
  // A grouped multi-city cart item is one item whose `pairedTripItemIds` cover
  // every leg, so it matches each leg row and carries the whole-itinerary total.
  // Apply that total to the first matching row only — pricing later legs with the
  // same total would inflate the footer sum (a 3-leg A$461 itinerary footing as
  // A$1,383). Tracked by item identity since the grouped item has no cartItemId.
  const pricedCartItems = new Set<CartPanelItem>()

  return rows.map(row => {
    if (!shouldUseCartPanelPricing(row)) return row

    const cartPanelItem = cartPanelItems.find(item => isTripListRowMatchingCartPanelItem(row, item))
    if (!cartPanelItem || !isCartPanelPriceUsable(row.price, cartPanelItem)) return row

    if (pricedCartItems.has(cartPanelItem)) {
      return { ...row, price: { ...row.price, price: undefined, memberPrice: undefined, totalPrice: undefined, totalMemberPrice: undefined } }
    }
    pricedCartItems.add(cartPanelItem)

    return {
      ...row,
      price: getCartPanelRowPrice(row.price, cartPanelItem, currentCurrency),
    }
  })
}
